import React, { useMemo, useState } from "react";
import { Alert, Grid2, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTranslate } from "../../../hooks/useTranslate";
import SummaryBox from "../../../components/SummaryBox";
import TaskCard from "../../../components/TaskCard";
import Chip from "../../../components/Chip";
import EnrollmentProgress from "../../../components/EnrollmentProgress";
import StatusBox from "../../../components/StatusBox";
import { useStudents } from "../../../context/students";
import { formatTo, localeDateFormat } from "../../../utils/dateTimeHelper";
import useStatuses from "../../../context/status/useStatuses";
import { useGetGeneralStatuses } from "../../../hooks/useGetGeneralStatuses";
import { isVersionUS, isVersionUK } from "../../../services/versionService";
import SubjectsSelection from "./components/SubjectsSelection";
import { applicantTasksHelper } from "../../../helpers/applicant.tasks.helper";
import "./Dashboard.css";

const Dashboard = () => {
    const { t } = useTranslate();
    const { activeStudent } = useStudents();
    const {
        statuses, isLockedStatus, generalStatuses, stepsProgress, showConsultationCall,
        hideSubjectSelectionUS,
        isRequiredOutOfYear, isRequiredDBS, isSchoolReportsRequired,
        isLockingConfirmationOpen, setIsOpenLockingModal,
    } = useStatuses();
    const { general, isLoading } = useGetGeneralStatuses(activeStudent.studentId);

    const [editSubjectSelection, setEditSubjectSelection] = useState(false);

    const onSubjectsEdit = () => {
        setEditSubjectSelection(!editSubjectSelection);
    };

    const taskPanels = useMemo(() => applicantTasksHelper.generateTaskPanels({
        statuses,
        isLockedStatus,
        generalStatuses,
        isRequiredDBS,
        isRequiredOutOfYear,
        isSchoolReportsRequired,
        hideSubjectSelectionUS,
        showConsultationCall,
    }), [
        statuses,
        isLockedStatus,
        generalStatuses,
        isRequiredDBS,
        isRequiredOutOfYear,
        isSchoolReportsRequired,
        hideSubjectSelectionUS,
        showConsultationCall,
    ]);

    const taskPanelsKeys = useMemo(() => taskPanels.map((task) => task.key), [taskPanels]);

    return (
        <div className="page-content">
            {isLockingConfirmationOpen ? (
                <SummaryBox className="bg-blue-light mb-8" contentClassName="w-full flex gap-4 align-center justify-between">
                    <div>
                        <Typography variant="h4" component="div" className="text-primary">
                            {t('lockingConfirmation.alertTitle')}
                        </Typography>
                        <Typography variant="body1" component="div">
                            {t('lockingConfirmation.alertSubtitle')}
                        </Typography>
                    </div>
                    <Button
                        variant="contained"
                        className="button-accent w-320"
                        onClick={() => setIsOpenLockingModal(true)}
                    >
                        {t("lockingConfirmation.progressBtn")}
                    </Button>
                </SummaryBox>
            ) : null}
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Typography variant="h1">
                        {t("dashboard.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4" component="div">
                        {t("dashboard.subtitle")}
                        <br/>
                        <br/>
                        <b>{t("dashboard.subtitleBold")}</b>
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <EnrollmentProgress
                        isLoading={isLoading}
                        stepsProgress={stepsProgress}
                        taskPanelsKeys={taskPanelsKeys}
                        school={general.school}
                        studySchedule={general.student?.studySchedule}
                    />
                </Grid2>
            </Grid2>
            <SummaryBox title={t("dashboard.studentSummary")} className="mt-16 mb-12">
                <Typography variant="h4">{`${activeStudent.forename} ${activeStudent.surname}`}</Typography>
                {isVersionUS && (
                    <Typography variant="h5">
                        <b className="font-bold">{t("dashboard.pupilID")}</b>
                        {` ${activeStudent.studentId}`}
                    </Typography>
                )}
                <Typography variant="h5">
                    <b className="font-bold">{t("dashboard.requestedStartDate")}</b>
                    {` ${formatTo(activeStudent.desiredStartDate, localeDateFormat, "yyyy-MM-dd")}`}
                </Typography>
                {(generalStatuses.student?.responsibleAdults && generalStatuses.student?.responsibleAdults.length > 0) && (
                    <Typography variant="h5">
                        <b className="font-bold">{t("dashboard.responsibleAdults")}</b>
                        {generalStatuses.student.responsibleAdults.map((adult) => `${adult.title ? `${adult.title} ` : ""}${adult.firstName} ${adult.lastName}`).join(", ")}
                    </Typography>
                )}
                {isVersionUK && (
                    <>
                        {generalStatuses.student?.division && (
                            <Typography variant="h5">
                                <b className="font-bold">{t("dashboard.division")}</b>
                                {` ${generalStatuses.student?.division}`}
                            </Typography>
                        )}
                        <Typography variant="h5">
                            <b className="font-bold">{t("dashboard.yearGroup")}</b>
                            {` ${generalStatuses.student?.yearGroupName}`}
                        </Typography>
                    </>
                )}
                <div className="flex gap-1">
                    <Typography variant="h5" className="shrink-0">
                        <b className="font-bold">{t("dashboard.approvedSubject")}</b>
                    </Typography>
                    <Typography variant="h5" className="edit-button">
                        {generalStatuses.student?.approvedSubjects && generalStatuses.student.approvedSubjects?.join(", ")}
                        {(isVersionUK && !isLockedStatus.responsibleAdults) && (
                            <Button className="btn-link ml-2" onClick={onSubjectsEdit}>
                                {t(editSubjectSelection ? "dashboard.subjectsEditCancel" : "dashboard.subjectsEdit")}
                            </Button>
                        )}
                    </Typography>
                </div>
                {editSubjectSelection && <SubjectsSelection onClose={() => setEditSubjectSelection(false)}/>}
            </SummaryBox>
            <Grid2 container spacing={3}>
                {taskPanels.map((taskPanel) => (
                    <Grid2
                        key={taskPanel.titleKey}
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 6,
                            lg: 4,
                        }}
                    >
                        <TaskCard
                            title={t(taskPanel.titleKey)}
                            icon={taskPanel.icon}
                            isSuccess={taskPanel.isSuccess}
                            isLocked={taskPanel.isLocked}
                            isDisabled={taskPanel.isDisabled}
                        >
                            <div className="flex flex-column gap-2">
                                {taskPanel.tasks.map((task) => (
                                    <div key={task.titleKey} className="flex align-center justify-between gap-2 w-full">
                                        {t(task.titleKey)}
                                        {task.statusKey && (
                                            <Chip
                                                isWarning={task.isWarning}
                                                isSuccess={task.isSuccess}
                                                isInfo={task.isInfo}
                                            >
                                                {t(task.statusKey)}
                                            </Chip>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </TaskCard>
                    </Grid2>
                ))}
            </Grid2>
        </div>
    );
};

export default Dashboard;
